// Button-opens-list select for short enum lists (state, account type,
// ownership type, owner title). No typing — that's the combobox
// (`SearchableSelect`). The placeholder option stays at the top so
// the field can be cleared after a choice.

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { SearchableOption } from "./SearchableSelect";

export type SelectOption<T extends string = string> = SearchableOption<T>;

interface Props<T extends string> {
  id?: string;
  value: T | "";
  options: readonly SelectOption<T>[];
  onChange: (value: T | "") => void;
  placeholder?: string;
  required?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Fallback label when `value` is set but not in `options`. */
  selectedLabel?: string;
}

export function SelectField<T extends string>({
  id,
  value,
  options,
  onChange,
  placeholder = "Select one…",
  required,
  ariaInvalid,
  ariaDescribedBy,
  ariaLabel,
  disabled = false,
  selectedLabel,
}: Props<T>) {
  const autoId = useId();
  const rootId = id ?? `sf-${autoId}`;
  const listboxId = `${rootId}-listbox`;

  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );
  const display = selected?.label ?? selectedLabel ?? "";
  const items: readonly SelectOption<T | "">[] = useMemo(
    () => [{ value: "", label: placeholder }, ...options],
    [options, placeholder],
  );

  useEffect(() => {
    if (!open) return;
    const idx = items.findIndex((o) => o.value === value);
    setHighlight(idx >= 0 ? idx : 0);
  }, [open, items, value]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLLIElement>(
      `#${rootId}-opt-${highlight}`,
    );
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlight, open, rootId]);

  function commit(next: T | "") {
    onChange(next);
    setOpen(false);
    buttonRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlight((h) =>
        e.key === "ArrowDown"
          ? Math.min(items.length - 1, h + 1)
          : Math.max(0, h - 1),
      );
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const opt = items[highlight];
      if (opt) commit(opt.value);
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    } else if (e.key === "Tab") {
      if (open) setOpen(false);
    }
  }

  return (
    <div
      ref={wrapperRef}
      className={`relative ${disabled ? "opacity-50" : ""}`}
    >
      <div className="relative">
        <button
          ref={buttonRef}
          id={rootId}
          type="button"
          className={`input cursor-pointer pr-9 text-left ${display ? "" : "text-brand-placeholder"}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && items[highlight]
              ? `${rootId}-opt-${highlight}`
              : undefined
          }
          aria-invalid={ariaInvalid || undefined}
          aria-describedby={ariaDescribedBy}
          aria-label={ariaLabel}
          aria-required={required || undefined}
          disabled={disabled}
          onClick={() => {
            if (!disabled) setOpen((v) => !v);
          }}
          onKeyDown={onKeyDown}
        >
          {display || placeholder}
        </button>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-muted"
        />
      </div>
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          className="absolute z-30 mt-1.5 max-h-72 w-full overflow-auto rounded-lg border border-brand-border bg-brand-surface-2 p-1 shadow-pop"
        >
          {items.map((opt, idx) => (
            <li
              key={opt.value === "" ? "__placeholder__" : opt.value}
              id={`${rootId}-opt-${idx}`}
              role="option"
              aria-selected={opt.value === value}
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(opt.value);
              }}
              className={`cursor-pointer rounded-md px-3 py-2 text-sm transition-colors ${
                idx === highlight
                  ? "bg-brand-blue/25 text-white"
                  : opt.value === ""
                    ? "text-brand-muted"
                    : "text-brand-text"
              }`}
            >
              <div>{opt.label}</div>
              {opt.hint && (
                <div className="text-xs text-brand-muted">{opt.hint}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
